import { motion } from 'framer-motion';
import { Star, MessageSquare } from 'lucide-react';
import { RatingWithUser } from '@/hooks/useFigurineDetail';
import RatingStars from '@/components/RatingStars';

interface FigurineReviewsProps {
  reviews: RatingWithUser[];
  figurineId: string;
  avgRating: number;
  ratingCount: number;
  onAuthRequired: () => void;
}

export default function FigurineReviews({ reviews, figurineId, avgRating, ratingCount, onAuthRequired }: FigurineReviewsProps) {
  const dist = [5,4,3,2,1].map(s => ({
    stars: s,
    count: reviews.filter(r => r.stars === s).length,
    pct: ratingCount > 0 ? (reviews.filter(r => r.stars === s).length / ratingCount) * 100 : 0,
  }));

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-black text-foreground" style={{ fontFamily: 'Orbitron, sans-serif' }}>
        OPINIE
      </h2>

      {/* Summary */}
      <div className="flex flex-col sm:flex-row gap-6 p-5 rounded-2xl border"
        style={{ background: 'oklch(0.16 0.03 280)', borderColor: 'oklch(0.25 0.045 280)' }}>
        <div className="flex flex-col items-center justify-center gap-1 min-w-[100px]">
          <span className="text-6xl font-black" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--chart-4)' }}>
            {ratingCount > 0 ? avgRating.toFixed(1) : '—'}
          </span>
          <div className="flex gap-0.5">
            {[1,2,3,4,5].map(s => (
              <Star key={s} className="w-4 h-4" style={{ fill: s <= Math.round(avgRating) ? 'var(--chart-4)' : 'transparent', color: s <= Math.round(avgRating) ? 'var(--chart-4)' : 'var(--muted-foreground)' }} />
            ))}
          </div>
          <span className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {ratingCount} {ratingCount === 1 ? 'ocena' : ratingCount < 5 ? 'oceny' : 'ocen'}
          </span>
        </div>

        <div className="flex-1 space-y-1.5">
          {dist.map(({ stars, count, pct }) => (
            <div key={stars} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-4 text-right" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stars}</span>
              <Star className="w-3 h-3 flex-shrink-0" style={{ fill: 'var(--chart-4)', color: 'var(--chart-4)' }} />
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'oklch(0.22 0.04 280)' }}>
                <motion.div className="h-full rounded-full" style={{ background: 'var(--chart-4)', width: `${pct}%` }}
                  initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, delay: (5 - stars) * 0.07 }} />
              </div>
              <span className="text-xs text-muted-foreground w-5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Your rating CTA */}
      <div className="p-4 rounded-xl border" style={{ background: 'oklch(0.14 0.028 280)', borderColor: 'oklch(0.22 0.04 280)' }}>
        <p className="text-sm text-muted-foreground mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          Oceń tę figurkę:
        </p>
        <RatingStars figurineId={figurineId} avgRating={avgRating} ratingCount={ratingCount} size="md" onAuthRequired={onAuthRequired} />
      </div>

      {/* Review list */}
      {reviews.length === 0 ? (
        <div className="flex flex-col items-center py-10 text-muted-foreground gap-3">
          <MessageSquare className="w-10 h-10 opacity-30" />
          <p style={{ fontFamily: 'Rajdhani, sans-serif' }}>Brak ocen. Bądź pierwszy!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r, i) => (
            <motion.div key={r.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-start gap-3 p-4 rounded-xl border"
              style={{ background: 'oklch(0.16 0.03 280)', borderColor: 'oklch(0.22 0.04 280)' }}>
              <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center font-black text-sm text-white"
                style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}>
                {(r.username?.[0] ?? '?').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-bold text-sm text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    {r.username ?? 'Anonimowy'}
                  </span>
                  <span className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {new Date(r.created_at).toLocaleDateString('pl-PL')}
                  </span>
                </div>
                <div className="flex gap-0.5 mt-1">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className="w-3.5 h-3.5" style={{ fill: s <= r.stars ? 'var(--chart-4)' : 'transparent', color: s <= r.stars ? 'var(--chart-4)' : 'var(--muted-foreground)' }} />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
